/* ============================================================
   BNE STUDIO — shared nav + mobile sheet
   Ported from the design handoff (chrome.js NAV + SHEET). The
   open/close dropdown + sheet behaviour is wired by
   useNightMotion (it toggles the .open class, matching the
   original CSS-driven markup).

   `active` highlights the current product. Links to product
   pages that don't exist yet point to "#"; Distribution is the
   only real route so it uses react-router.
   ============================================================ */
import React from "react";
import { Link } from "react-router-dom";
import { NightIcon } from "./icons";

const DIST = "/distribute";

const PRODUCT_KEYS = ["dist", "contracts", "links", "sync", "vision", "live"];
const TOOL_KEYS = ["tools", "stem", "mix", "suite", "lyrics", "bpm", "pitch"];

// Internal route -> react-router Link; everything else -> plain anchor.
function NavLink({ href, className, children }) {
  if (href && href.charAt(0) === "/") {
    return (
      <Link to={href} className={className}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} className={className}>
      {children}
    </a>
  );
}

function Prod({ href, ic, title, desc, on }) {
  return (
    <NavLink href={href} className={on ? "dd-prod on" : "dd-prod"}>
      <span className="ico" style={{ color: `var(--p-${ic})` }}>
        <NightIcon name={ic} />
      </span>
      <span>
        <h6>{title}</h6>
        <p>{desc}</p>
      </span>
    </NavLink>
  );
}

function Mini({ href, ic, label }) {
  const col = ic === "links" ? "links" : ic === "lyrics" ? "lyrics" : "tools";
  return (
    <NavLink href={href} className="dd-m">
      <span className="ico" style={{ color: `var(--p-${col})` }}>
        <NightIcon name={ic} />
      </span>
      <b>{label}</b>
    </NavLink>
  );
}

function LoginRow({ name, meta, href }) {
  return (
    <a href={href}>
      <span>{name}</span>
      <span className="meta">{meta}</span>
    </a>
  );
}

function MLink({ href, ic, label, on }) {
  return (
    <NavLink href={href} className={on ? "mlink on" : "mlink"}>
      <span className="ico" style={{ color: `var(--p-${ic})` }}>
        <NightIcon name={ic} />
      </span>
      {label}
    </NavLink>
  );
}

const Brand = () => (
  <Link className="brand brand-lockup" to="/home" aria-label="BNE Studio home">
    <span className="mark">
      <NightIcon name="logo" />
    </span>
  </Link>
);

export default function NightNav({ active = "" }) {
  const productActive = PRODUCT_KEYS.indexOf(active) !== -1;
  const toolsActive = TOOL_KEYS.indexOf(active) !== -1;

  return (
    <>
      <nav className="nav">
        <div className="wrap">
          <Brand />
          <div className="nav-spacer" />

          <div className="nav-links">
            {/* Products */}
            <div className={productActive ? "nav-item active" : "nav-item"}>
              <button aria-haspopup="true" aria-expanded="false">
                Products <NightIcon name="chev" />
              </button>
              <div className="dd dd-products">
                <div className="dd-panel">
                  <div className="dd-cols">
                    <div>
                      <div className="dd-g">Release</div>
                      <Prod href={DIST} ic="dist" title="Distribution" desc="Release worldwide, manage catalog" on={active === "dist"} />
                      <Prod href="/contracts" ic="contracts" title="Contracts" desc="Music agreements & documents" on={active === "contracts"} />
                      <div className="dd-g" style={{ marginTop: "8px" }}>Promote</div>
                      <Prod href="/tools#smart-links" ic="links" title="Smart Links" desc="One link for every campaign" on={active === "links"} />
                    </div>
                    <div>
                      <div className="dd-g">Create</div>
                      <Prod href="/sync" ic="sync" title="Sync" desc="Stems, approvals, collaboration" on={active === "sync"} />
                      <Prod href="/vision" ic="vision" title="Vision" desc="Turn a song into video" on={active === "vision"} />
                      <div className="dd-g" style={{ marginTop: "8px" }}>Perform</div>
                      <Prod href="/live" ic="live" title="BNE Live" desc="Create events, sell tickets" on={active === "live"} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tools */}
            <div className={toolsActive ? "nav-item active" : "nav-item"}>
              <button aria-haspopup="true" aria-expanded="false">
                Tools <NightIcon name="chev" />
              </button>
              <div className="dd dd-products" style={{ width: "520px" }}>
                <div className="dd-panel">
                  <div className="dd-g">Featured tools</div>
                  <div className="dd-mini">
                    <Mini href="/tools#stem-separator" ic="stem" label="Stem Separator" />
                    <Mini href="/tools#mix-feedback" ic="mix" label="Mix Feedback" />
                    <Mini href="/tools#smart-links" ic="links" label="Smart Links" />
                    <Mini href="/tools#music-suite" ic="suite" label="Music Suite" />
                  </div>
                  <div className="dd-sep" />
                  <div className="dd-g">Community &amp; audio helpers</div>
                  <div className="dd-mini">
                    <Mini href="/tools#lyrics" ic="lyrics" label="Lyrics" />
                    <Mini href="/tools#bpm-finder" ic="bpm" label="BPM Finder" />
                    <Mini href="/tools#pitch-shifter" ic="pitch" label="Pitch Shifter" />
                  </div>
                  <div className="dd-sep" />
                  <Link className="dd-all" to="/tools">
                    Browse all tools <NightIcon name="ext" />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="nav-spacer" />

          <div className="nav-cta">
            <div className="nav-item">
              <button className="btn btn-ghost" style={{ fontWeight: 500 }} aria-haspopup="true" aria-expanded="false">
                Log in
                <svg viewBox="0 0 24 24" fill="none" style={{ width: "12px", height: "12px" }}>
                  <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <div className="dd dd-login">
                <div className="dd-panel">
                  <LoginRow name="Distribution Dashboard" meta="dashboard.bne.studio" href="https://dashboard.bne.studio/login" />
                  <LoginRow name="Sync Workspace" meta="one.bne.studio" href="https://one.bne.studio" />
                  <LoginRow name="Vision" meta="vision.bne.studio" href="https://vision.bne.studio" />
                  <LoginRow name="Contracts" meta="contracts.bne.studio" href="https://contracts.bne.studio" />
                  <LoginRow name="BNE Live" meta="bne.live" href="https://bne.live" />
                  <div className="dd-sep" />
                  <LoginRow name="Links &amp; Tools" meta="bne.to" href="https://bne.to" />
                </div>
              </div>
            </div>
            <Link className="btn btn-primary" to={DIST}>
              Start free
            </Link>
            <div className="burger" id="burger">
              <span />
              <span />
              <span />
            </div>
          </div>
        </div>
      </nav>

      {/* mobile sheet */}
      <div className="msheet" id="msheet">
        <div className="msheet-top">
          <Brand />
          <button className="msheet-close">&times;</button>
        </div>
        <div className="msheet-body">
          <h5>Release</h5>
          <MLink href={DIST} ic="dist" label="Distribution" on={active === "dist"} />
          <MLink href="/contracts" ic="contracts" label="Contracts" on={active === "contracts"} />
          <h5>Create</h5>
          <MLink href="/sync" ic="sync" label="Sync" on={active === "sync"} />
          <MLink href="/vision" ic="vision" label="Vision" on={active === "vision"} />
          <h5>Perform</h5>
          <MLink href="/live" ic="live" label="BNE Live" on={active === "live"} />
          <h5>More</h5>
          <MLink href="/tools" ic="tools" label="Tools" on={active === "tools"} />
        </div>
        <div className="msheet-foot">
          <Link className="btn btn-primary btn-lg" to={DIST}>
            Start free
          </Link>
          <a className="btn btn-ghost btn-lg" href="https://dashboard.bne.studio/login">
            Log in
          </a>
        </div>
      </div>
    </>
  );
}
